import {
    HOLIDAY_REGION,
    HOLIDAY_REGION_KEYWORDS,
    HOLIDAY_REGION_ORDER,
    type HolidayRegion
} from '@/src/constants/holidays'
import type { HolidayImage, HolidayPackageSummary, HolidayPrice } from '@/src/types/holidays'

export interface HolidayRegionSummary {
    region: HolidayRegion
    count: number
    fromPrice: HolidayPrice
    image?: HolidayImage
}

const normalise = (value: string) => ` ${value.toLowerCase().replace(/[^a-z]+/g, ' ')} `

export const resolveHolidayRegion = (summary: HolidayPackageSummary): HolidayRegion => {
    const haystack = normalise(`${summary.destinationName} ${summary.slug}`)
    const match = HOLIDAY_REGION_KEYWORDS.find(({ keywords }) =>
        keywords.some(keyword => haystack.includes(` ${keyword} `))
    )
    return match?.region ?? HOLIDAY_REGION.OTHER
}

/** One entry per region that has packages, in display order; image and price come from its cheapest package. */
export const buildHolidayRegionSummaries = (packages: HolidayPackageSummary[]): HolidayRegionSummary[] =>
    HOLIDAY_REGION_ORDER.flatMap(region => {
        const inRegion = packages.filter(summary => resolveHolidayRegion(summary) === region)
        if (!inRegion.length) return []

        const cheapest = inRegion.reduce((low, summary) =>
            summary.startingPrice.amount < low.startingPrice.amount ? summary : low
        )
        return [
            {
                region,
                count: inRegion.length,
                fromPrice: cheapest.startingPrice,
                image: cheapest.thumbnail ?? inRegion.find(summary => summary.thumbnail)?.thumbnail
            }
        ]
    })

/** Keeps the incoming (already sorted) order. */
export const filterHolidayPackagesByRegion = (
    packages: HolidayPackageSummary[],
    regions: HolidayRegion[]
): HolidayPackageSummary[] => packages.filter(summary => regions.includes(resolveHolidayRegion(summary)))
